import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import FilterListIcon from '@mui/icons-material/FilterList'
import type { AudienceFilters } from '@brushfire/types'
import { SignalProviderDemographicFiltersPanel } from './SignalProviderDemographicFiltersPanel'
import { hasActiveDemographicFilters } from '../lib/signal-provider-list-filters'
import {
  SP_LIST_DEMOGRAPHIC_PANEL_BORDER,
  SP_LIST_DEMOGRAPHIC_PANEL_BG,
  SP_LIST_DEMOGRAPHIC_PANEL_HEADER_BOTTOM_PX,
  SP_LIST_DEMOGRAPHIC_PANEL_PADDING_TOP_PX,
  SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX,
  SP_LIST_DEMOGRAPHIC_PANEL_SHADOW,
  SP_LIST_FILTERS_BUTTON_WIDTH_PX,
  spListHeaderFilterTriggerLabelSx,
  spListHeaderFilterTriggerSx,
} from '../lib/signal-provider-filter-layout'

interface Props {
  filters: AudienceFilters
  onChange: (next: AudienceFilters) => void
  onClearAll: () => void
}

/** Figma `37:901` trigger + `37:792` popover panel. */
export function SignalProviderFiltersPopover({ filters, onChange, onClearAll }: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const hasFilters = hasActiveDemographicFilters(filters)

  return (
    <>
      <ButtonBase
        data-testid="signal-provider-filters-button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          ...spListHeaderFilterTriggerSx,
          width: { xs: '100%', sm: SP_LIST_FILTERS_BUTTON_WIDTH_PX },
          minWidth: { xs: '100%', sm: SP_LIST_FILTERS_BUTTON_WIDTH_PX },
          borderColor: open || hasFilters ? '#008cd0' : spListHeaderFilterTriggerSx.borderColor,
        }}
      >
        <Typography component="span" sx={spListHeaderFilterTriggerLabelSx}>
          Filters
        </Typography>
        <FilterListIcon sx={{ fontSize: 16, color: '#64748b', flexShrink: 0 }} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              bgcolor: SP_LIST_DEMOGRAPHIC_PANEL_BG,
              borderRadius: '10px',
              border: `0.5px solid ${SP_LIST_DEMOGRAPHIC_PANEL_BORDER}`,
              boxShadow: SP_LIST_DEMOGRAPHIC_PANEL_SHADOW,
              overflow: 'hidden',
            },
          },
        }}
        data-testid="signal-provider-filters-popover"
      >
        <Box
          sx={{
            bgcolor: SP_LIST_DEMOGRAPHIC_PANEL_BG,
            px: `${SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX}px`,
            pt: `${SP_LIST_DEMOGRAPHIC_PANEL_PADDING_TOP_PX}px`,
            pb: `${SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX}px`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#000000',
            }}
          >
            Filter Signal Providers
          </Typography>
          <Button
            size="small"
            onClick={onClearAll}
            data-testid="signal-provider-filters-clear-all"
            sx={{
              textTransform: 'none',
              color: '#1976d2',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              minWidth: 0,
              p: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Clear All
          </Button>
        </Box>
        <Box sx={{ pb: `${SP_LIST_DEMOGRAPHIC_PANEL_HEADER_BOTTOM_PX}px`, bgcolor: SP_LIST_DEMOGRAPHIC_PANEL_BG }}>
          <SignalProviderDemographicFiltersPanel filters={filters} onChange={onChange} />
        </Box>
      </Popover>
    </>
  )
}
